import { useCallback, useState } from "react";
import { ASSET_TYPE } from "domains/assets/utils/getIsType";
import { capitalizeFirstWord } from "domains/commons/misc";
import { UseImageUploadDragDropProps } from "domains/image/hooks/useImageUploadDragDrop";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import {
  GetAssetsByAssetIdApiResponse,
  useLazyGetAssetsByAssetIdQuery,
} from "infra/api/generated/api";

interface UseScenarioAssetDropProps {
  onAssetFound?: (
    asset: GetAssetsByAssetIdApiResponse["asset"]
  ) => Promise<void>;
  kind?: "image" | "video" | "3d";
}

export const useScenarioAssetDrop = ({
  onAssetFound,
  kind,
}: UseScenarioAssetDropProps) => {
  const [getAssetById] = useLazyGetAssetsByAssetIdQuery();
  const { selectedProject } = useTeamContext();
  const { successToast, errorToast } = useScenarioToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleAssetUrl = useCallback<
    NonNullable<UseImageUploadDragDropProps["onImageDrop"]>
  >(
    async ({ assetId }) => {
      if (!assetId) return;

      setIsLoading(true);
      try {
        let getAssetData = await getAssetById({
          projectId: selectedProject.id,
          assetId,
        });

        if (getAssetData.data?.asset) {
          if (
            kind === "video" &&
            getAssetData.data.asset.kind === "image" &&
            getAssetData.data.asset.metadata.name === "image.png" &&
            getAssetData.data.asset.metadata.parentId
          ) {
            getAssetData = await getAssetById({
              projectId: selectedProject.id,
              assetId: getAssetData.data.asset.metadata.parentId,
            });
            if (!getAssetData.data?.asset) {
              throw new Error("Asset not found");
            }
          }

          if (kind && getAssetData.data.asset.kind !== kind) {
            errorToast({
              title: `Invalid asset type`,
              description: `The asset should be of type "${ASSET_TYPE[kind].singular}"`,
            });
            return;
          }
          await onAssetFound?.(getAssetData.data.asset);
          successToast({
            title: `${capitalizeFirstWord(
              ASSET_TYPE[kind ?? "image"].singular
            )} imported successfully`,
          });
        } else {
          throw new Error("Asset not found");
        }
      } catch (error) {
        const isAssetNotFound =
          error instanceof Error && error.message === "Asset not found";
        errorToast({
          title: `Error importing ${ASSET_TYPE[kind ?? "image"].singular}`,
          description: isAssetNotFound
            ? `${capitalizeFirstWord(
                ASSET_TYPE[kind ?? "image"].singular
              )} not found`
            : `There was an error importing the ${
                ASSET_TYPE[kind ?? "image"].singular
              }. Please try again.`,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      getAssetById,
      selectedProject.id,
      onAssetFound,
      successToast,
      errorToast,
      kind,
    ]
  );

  return {
    handleAssetUrl,
    isLoading,
  };
};
